// App-wide owner of in-flight chat turns.
//
// A chat turn runs server-side as a background "run" (FastAPI ChatRun) that
// outlives the HTTP connection. This context holds the client half: one SSE
// reader per session that keeps going while the user switches chats or
// routes, re-attaches after a dropped connection (resuming after the last
// `run_seq`), and on mount re-attaches to every run still going on the
// server, so a reload or a reopened tab picks up where it left off.
//
// Each entry holds only the in-flight turn — `messages` is the
// [user, assistant] pair — and ChatApp renders it after the chat's stored
// messages. When a run ends the entry stays in a terminal status until the
// chat that shows it folds the pair into its own state and dismisses it.

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { MessageData } from '../features/chat/MessageList';
import {
  attachChatRun,
  cancelChatRun,
  listChatRuns,
  RunGoneError,
  streamChat,
  type ChatStreamEvent,
  type StreamChatBody,
} from '../lib/sse';
import { useSessionContext } from './SessionContext';
import { deriveTitle } from '../lib/sessions';
import { emptyAccumulatedGraph, mergeGraphEvent } from '../lib/graphMerge';
import { getChat, updateAccumulatedGraph, type ChatDetail } from '../lib/feedbackApi';
import { chatKey } from '../lib/queries';
import { turnsToStoredMessages } from '../lib/chatAdapters';
import { isHeartbeat } from '../features/chat/timelineModel';
import type {
  AccumulatedGraph,
  PipelineStep,
  PlanningEvent,
  RoutingEvent,
  StepDetail,
  SourceDoc,
  GraphTraversalEvent,
  ProfileEvent,
  ScoreThreshold,
  DetectedIOC,
  StoredMessage,
} from '../types';

/** `gone`: the server no longer knows the run (evicted, or lost to a
 *  restart) — the chat must re-read its turns from the server. */
export type StreamStatus = 'queued' | 'streaming' | 'done' | 'error' | 'cancelled' | 'gone';

export function isLiveStatus(s: StreamStatus | undefined): boolean {
  return s === 'queued' || s === 'streaming';
}

export interface ActiveStream {
  sessionId: string;
  /** Server run id (== turn_id). Absent for temporary chats and until the
   *  first `turn_id` frame arrives. */
  runId?: string;
  serverChatId: string | undefined;
  status: StreamStatus;
  queuePosition?: number;
  queueAhead?: number;
  /** The in-flight turn only: [user, assistant]. */
  messages: MessageData[];
  accGraph: AccumulatedGraph;
  userMsgId: string;
  assistantMsgId: string;
  hasUnseenGraphData: boolean;
  /** For status `gone`: the chat's turns as the server has them now. */
  serverMessages?: StoredMessage[];
  serverGraph?: AccumulatedGraph;
}

export interface StartStreamArgs {
  sessionId: string;
  body: StreamChatBody;
  initialAccGraph: AccumulatedGraph;
  userMsg: MessageData;
  assistantMsg: MessageData;
  serverChatId: string | undefined;
  startSessionTitle: string | undefined;
  /** Number of user turns before this one (graph merge provenance). */
  priorUserTurns: number;
}

export interface AttachRunArgs {
  sessionId: string;
  runId: string;
  query: string;
}

export interface ActiveStreamsContextValue {
  subscribe: (listener: () => void) => () => void;
  getStream: (sessionId: string | null | undefined) => ActiveStream | undefined;
  /** Session ids with a live (queued/streaming) run. Stable between changes. */
  getRunningIds: () => ReadonlySet<string>;
  startStream: (args: StartStreamArgs) => void;
  /** Re-attach to a server-side run (no-op if already tracked). */
  attachRun: (args: AttachRunArgs) => void;
  /** Stop: cancels the run server-side, then lets the stream finish. */
  stopStream: (sessionId: string) => void;
  /** Drop a terminal entry once its chat has folded it in. */
  dismissStream: (sessionId: string) => void;
  updateStreamGraph: (sessionId: string, next: AccumulatedGraph) => void;
  clearStreamUnseenGraph: (sessionId: string) => void;
}

const ActiveStreamsContext = createContext<ActiveStreamsContextValue | null>(null);

const MAX_REATTACH = 4;

// Duplicated from ChatApp.tsx — keep the two implementations in sync.
function messageDataToStored(messages: MessageData[]): StoredMessage[] {
  return messages
    .filter((m) => !m.streaming)
    .map((m) => ({
      id: m.id,
      role: m.role,
      content: m.content,
      sources: m.sources,
      completedSteps: m.completedSteps,
      stepDetails: m.stepDetails,
      planningEvent: m.planningEvent ?? null,
      routingEvent: m.routingEvent ?? null,
      graphTraversalEvent: m.graphTraversalEvent ?? null,
      profileEvent: m.profileEvent ?? null,
      scoreThreshold: m.scoreThreshold ?? null,
      queryDetectedIocs: m.queryDetectedIocs,
      responseDetectedIocs: m.responseDetectedIocs,
      turnId: m.turnId,
    }));
}

interface Control {
  ctrl: AbortController;
  cancelRequested: boolean;
  cancelSent: boolean;
}

interface RunSpec {
  sessionId: string;
  /** Opens the first event stream (POST /chat or a re-attach). */
  open: (signal: AbortSignal) => AsyncGenerator<ChatStreamEvent, void, void>;
  initialAccGraph: AccumulatedGraph | Promise<AccumulatedGraph>;
  userMsg: MessageData;
  assistantMsg: MessageData;
  serverChatId: string | undefined;
  runId?: string;
  startSessionTitle: string | undefined;
  priorUserTurns: number | Promise<number>;
  /** SPA-started turn (title derivation) vs a re-attached one. */
  reattached: boolean;
}

export function ActiveStreamsProvider({ children }: { children: React.ReactNode }) {
  const ctx = useSessionContext();
  const qc = useQueryClient();
  const navigate = useNavigate();

  // Live mirrors for the long-running async loops below.
  const activeSessionIdRef = useRef(ctx.activeSessionId);
  useEffect(() => {
    activeSessionIdRef.current = ctx.activeSessionId;
  }, [ctx.activeSessionId]);
  const sessionsRef = useRef(ctx.sessions);
  useEffect(() => {
    sessionsRef.current = ctx.sessions;
  }, [ctx.sessions]);
  const ctxRef = useRef(ctx);
  ctxRef.current = ctx;

  const streamsRef = useRef<Map<string, ActiveStream>>(new Map());
  const controlsRef = useRef<Map<string, Control>>(new Map());
  // Run ids this tab has already handled, so a stale `active_run` in a
  // cached chat detail never re-attaches (and re-toasts) a finished run.
  const seenRunIdsRef = useRef<Set<string>>(new Set());
  const runningIdsRef = useRef<ReadonlySet<string>>(new Set());
  const listenersRef = useRef<Set<() => void>>(new Set());

  const notify = useCallback(() => {
    const running = new Set<string>();
    for (const [id, s] of streamsRef.current) if (isLiveStatus(s.status)) running.add(id);
    const prev = runningIdsRef.current;
    if (running.size !== prev.size || [...running].some((id) => !prev.has(id))) {
      runningIdsRef.current = running;
    }
    for (const l of listenersRef.current) l();
  }, []);

  const subscribe = useCallback((listener: () => void) => {
    listenersRef.current.add(listener);
    return () => {
      listenersRef.current.delete(listener);
    };
  }, []);

  const getStream = useCallback((sessionId: string | null | undefined) => {
    if (!sessionId) return undefined;
    return streamsRef.current.get(sessionId);
  }, []);

  const getRunningIds = useCallback(() => runningIdsRef.current, []);

  const replaceStream = useCallback(
    (sessionId: string, updater: (prev: ActiveStream | undefined) => ActiveStream | undefined) => {
      const prev = streamsRef.current.get(sessionId);
      const next = updater(prev);
      if (next === prev) return;
      if (next === undefined) streamsRef.current.delete(sessionId);
      else streamsRef.current.set(sessionId, next);
      notify();
    },
    [notify],
  );

  const sendCancel = useCallback((sessionId: string) => {
    const control = controlsRef.current.get(sessionId);
    const runId = streamsRef.current.get(sessionId)?.runId;
    if (!control || control.cancelSent) return;
    if (!runId) {
      // Temporary chats have no server run id; the server cancels them
      // when their only listener goes away.
      control.ctrl.abort();
      return;
    }
    control.cancelSent = true;
    cancelChatRun(runId).catch((err) => {
      // Couldn't reach the cancel endpoint: at least stop listening.
      console.warn('cancel failed', err);
      control.ctrl.abort();
    });
  }, []);

  const stopStream = useCallback(
    (sessionId: string) => {
      const control = controlsRef.current.get(sessionId);
      const entry = streamsRef.current.get(sessionId);
      if (!control || !entry || !isLiveStatus(entry.status)) return;
      control.cancelRequested = true;
      // Before the turn_id frame the run id is unknown; the loop sends the
      // cancel as soon as it arrives. Temporary chats never get one.
      if (entry.runId || entry.serverChatId === undefined) sendCancel(sessionId);
    },
    [sendCancel],
  );

  const dismissStream = useCallback(
    (sessionId: string) => {
      replaceStream(sessionId, (prev) => (prev && !isLiveStatus(prev.status) ? undefined : prev));
    },
    [replaceStream],
  );

  const updateStreamGraph = useCallback(
    (sessionId: string, next: AccumulatedGraph) => {
      replaceStream(sessionId, (prev) => (prev ? { ...prev, accGraph: next } : prev));
    },
    [replaceStream],
  );

  const clearStreamUnseenGraph = useCallback(
    (sessionId: string) => {
      replaceStream(sessionId, (prev) =>
        prev && prev.hasUnseenGraphData ? { ...prev, hasUnseenGraphData: false } : prev,
      );
    },
    [replaceStream],
  );

  const runStream = useCallback(
    (spec: RunSpec) => {
      const { sessionId, userMsg, assistantMsg } = spec;
      const existing = streamsRef.current.get(sessionId);
      if (existing && isLiveStatus(existing.status)) return;
      if (spec.runId) seenRunIdsRef.current.add(spec.runId);

      const control: Control = { ctrl: new AbortController(), cancelRequested: false, cancelSent: false };
      controlsRef.current.set(sessionId, control);
      const seedGraph =
        spec.initialAccGraph instanceof Promise ? emptyAccumulatedGraph() : spec.initialAccGraph;
      streamsRef.current.set(sessionId, {
        sessionId,
        runId: spec.runId,
        serverChatId: spec.serverChatId,
        status: 'streaming',
        messages: [userMsg, assistantMsg],
        accGraph: seedGraph,
        userMsgId: userMsg.id,
        assistantMsgId: assistantMsg.id,
        hasUnseenGraphData: false,
      });
      notify();

      // Accumulators survive re-attaches: a resumed stream continues after
      // the last run_seq, so nothing is replayed twice.
      let accContent = '';
      let accSources: SourceDoc[] = [];
      let accSteps: PipelineStep[] = [];
      let accStepDetails: StepDetail[] = [];
      let accPlanningTokens = '';
      let accPlanning: PlanningEvent | null = null;
      let accRouting: RoutingEvent | null = null;
      let accGraphEvent: GraphTraversalEvent | null = null;
      let accProfile: ProfileEvent | null = null;
      let accScoreThreshold: ScoreThreshold | null = null;
      let accResponseIocs: DetectedIOC[] | undefined;
      let accTurnId: string | undefined = spec.runId;
      let seededGraph: AccumulatedGraph = seedGraph;
      let errored = false;
      let cancelled = false;
      let lastSeq = 0; // last run_seq received — where a re-attach resumes
      let queryIndex = 0;

      const patchEntry = (fn: (prev: ActiveStream) => ActiveStream) =>
        replaceStream(sessionId, (prev) => (prev ? fn(prev) : prev));
      const patchAssistant = (patch: Partial<MessageData>) =>
        patchEntry((prev) => ({
          ...prev,
          messages: prev.messages.map((m) => (m.id === assistantMsg.id ? { ...m, ...patch } : m)),
        }));

      const handle = (ev: ChatStreamEvent) => {
        if (ev.type === 'done') return;
        if (typeof ev.run_seq === 'number') lastSeq = ev.run_seq;
        if (ev.type !== 'queued') {
          const cur = streamsRef.current.get(sessionId);
          if (cur?.status === 'queued') {
            patchEntry((prev) => ({ ...prev, status: 'streaming', queuePosition: undefined, queueAhead: undefined }));
          }
        }
        switch (ev.type) {
          case 'queued': {
            patchEntry((prev) => ({
              ...prev,
              status: ev.position > 1 || ev.ahead > 0 ? 'queued' : 'streaming',
              queuePosition: ev.position,
              queueAhead: ev.ahead,
            }));
            break;
          }
          case 'turn_id': {
            accTurnId = ev.turn_id;
            seenRunIdsRef.current.add(ev.turn_id);
            patchAssistant({ turnId: ev.turn_id });
            if (!spec.reattached) ctxRef.current.patchSession(sessionId, { serverChatId: ev.chat_id });
            patchEntry((prev) => ({ ...prev, runId: ev.turn_id, serverChatId: ev.chat_id }));
            if (control.cancelRequested) sendCancel(sessionId);
            break;
          }
          case 'token': {
            accContent += ev.content;
            patchAssistant({ content: accContent });
            break;
          }
          case 'sources': {
            accSources = ev.sources;
            patchAssistant({ sources: ev.sources });
            if (ev.min_score != null || ev.cut_score != null) {
              accScoreThreshold = {
                min_score: ev.min_score ?? null,
                cut_score: ev.cut_score ?? null,
                cutoff_dropped: ev.cutoff_dropped ?? 0,
                excluded_sources: ev.excluded_sources ?? [],
              };
              patchAssistant({ scoreThreshold: accScoreThreshold });
            }
            break;
          }
          case 'progress': {
            if (!accSteps.includes(ev.step)) accSteps = [...accSteps, ev.step];
            const { type: _t, run_seq: _rs, ...detail } = ev;
            void _t; void _rs;
            const next: StepDetail = { ...detail, ts: ev.ts ?? Date.now() / 1000 };
            // Every event is kept in arrival order, like the server's stored
            // step_details (start/done pairs, the answer's first-token event).
            // Only a run of heartbeats ("Still planning… (6s)") collapses
            // into its latest one.
            const last = accStepDetails[accStepDetails.length - 1];
            accStepDetails =
              last && last.step === next.step && isHeartbeat(last.message) && isHeartbeat(next.message)
                ? [...accStepDetails.slice(0, -1), next]
                : [...accStepDetails, next];
            patchAssistant({
              progressStep: ev.step,
              progressMessage: ev.message,
              progressCount: ev.count,
              completedSteps: accSteps,
              stepDetails: accStepDetails,
            });
            break;
          }
          case 'planning_token': {
            accPlanningTokens += ev.content;
            patchAssistant({ planningTokens: accPlanningTokens });
            break;
          }
          case 'planning': {
            const { type: _pt, run_seq: _ps, ...planning } = ev;
            void _pt; void _ps;
            accPlanning = planning as unknown as PlanningEvent;
            accPlanningTokens = ''; // Clear tokens once full plan arrives
            patchAssistant({ planningEvent: accPlanning, planningTokens: undefined });
            break;
          }
          case 'routing': {
            accRouting = { mode: ev.mode, confidence: ev.confidence, reasoning: ev.reasoning };
            patchAssistant({ routingEvent: accRouting, progressMessage: `Routing → ${ev.mode}` });
            break;
          }
          case 'graph_traversal': {
            const { type: _gt, run_seq: _gs, ...graph } = ev;
            void _gt; void _gs;
            accGraphEvent = graph as unknown as GraphTraversalEvent;
            patchAssistant({ graphTraversalEvent: accGraphEvent });
            const cur = streamsRef.current.get(sessionId);
            if (cur) {
              // Merge into the entry's graph (which user pin edits may
              // have changed via updateStreamGraph since the last event).
              const merged = mergeGraphEvent(cur.accGraph, accGraphEvent, queryIndex, userMsg.id, Date.now());
              const grew = merged.nodes.length > cur.accGraph.nodes.length;
              patchEntry((prev) => ({
                ...prev,
                accGraph: merged,
                hasUnseenGraphData: prev.hasUnseenGraphData || grew,
              }));
            }
            break;
          }
          case 'profile': {
            const { type: _pft, run_seq: _pfs, ...profile } = ev;
            void _pft; void _pfs;
            accProfile = profile as unknown as ProfileEvent;
            patchAssistant({ profileEvent: accProfile });
            break;
          }
          case 'detected_iocs': {
            if (ev.source === 'query') {
              patchEntry((prev) => ({
                ...prev,
                messages: prev.messages.map((m) =>
                  m.id === userMsg.id ? { ...m, queryDetectedIocs: ev.iocs } : m,
                ),
              }));
              // Also thread onto the assistant message so the retrieval card
              // (which renders for the assistant turn) can tie an IOC pivot
              // back to the indicator that triggered it.
              patchAssistant({ queryDetectedIocs: ev.iocs });
            } else {
              accResponseIocs = ev.iocs;
              patchAssistant({ responseDetectedIocs: ev.iocs });
            }
            break;
          }
          case 'cancelled': {
            cancelled = true;
            break;
          }
          case 'error': {
            errored = true;
            // A re-attach replays the run from the start; don't re-toast an
            // error the user already saw long ago.
            const ts = (ev as { ts?: number }).ts;
            if (!spec.reattached || ts === undefined || Date.now() / 1000 - ts < 15) {
              toast.error(ev.content);
            }
            patchAssistant({
              content: accContent || `Error: ${ev.content}`,
              streaming: false,
              progressStep: null,
              progressMessage: undefined,
              planningTokens: undefined,
            });
            break;
          }
        }
      };

      const finishGone = async () => {
        // The server forgot the run: whatever it persisted is in the chat
        // detail. Hand that to the chat instead of a half-streamed tail.
        try {
          const detail = await qc.fetchQuery<ChatDetail>({
            queryKey: chatKey(sessionId),
            queryFn: () => getChat(sessionId),
            staleTime: 0,
          });
          patchEntry((prev) => ({
            ...prev,
            status: 'gone',
            serverMessages: turnsToStoredMessages(detail.turns),
            serverGraph: (detail.accumulated_graph as AccumulatedGraph | null) ?? undefined,
          }));
        } catch {
          patchAssistant({ streaming: false, progressStep: null, progressMessage: undefined });
          patchEntry((prev) => ({ ...prev, status: 'error' }));
        }
      };

      const run = async () => {
        // Resolve the seed graph / turn index (a re-attach fetches them).
        try {
          queryIndex = await spec.priorUserTurns;
          if (spec.initialAccGraph instanceof Promise) {
            const g = await spec.initialAccGraph;
            seededGraph = g;
            patchEntry((prev) => ({ ...prev, accGraph: g }));
          }
        } catch {
          /* keep the empty seed */
        }

        let iter: AsyncGenerator<ChatStreamEvent, void, void> = spec.open(control.ctrl.signal);
        let attempts = 0;
        let sawDone = false;
        let aborted = false;
        let gone = false;
        let failure: string | null = null;
        while (true) {
          try {
            for await (const ev of iter) {
              if (ev.type === 'done') {
                sawDone = true;
                break;
              }
              attempts = 0;
              handle(ev);
            }
          } catch (err) {
            if ((err as Error)?.name === 'AbortError') aborted = true;
            else if (err instanceof RunGoneError) gone = true;
            else failure = err instanceof Error ? err.message : String(err);
          }
          if (sawDone || aborted || gone || errored) break;
          // The stream ended without [DONE]: dropped connection, proxy
          // timeout, server restart. The run may still be going — resume.
          const runId = streamsRef.current.get(sessionId)?.runId;
          if (!runId || attempts >= MAX_REATTACH) break;
          attempts += 1;
          await new Promise((r) => setTimeout(r, 500 * attempts));
          if (control.ctrl.signal.aborted) {
            aborted = true;
            break;
          }
          failure = null;
          iter = attachChatRun(runId, lastSeq, control.ctrl.signal);
        }
        controlsRef.current.delete(sessionId);

        if (gone) {
          await finishGone();
          ctxRef.current.notifyTurnComplete(sessionId);
          return;
        }
        if (!sawDone && !aborted && !errored) {
          errored = true;
          const msg = failure ?? 'Connection to the server was lost';
          toast.error(msg);
          patchAssistant({ content: accContent || `Error: ${msg}` });
        }
        if (aborted) cancelled = true;

        const finalSteps: PipelineStep[] =
          errored || cancelled || accSteps.includes('generating')
            ? accSteps
            : [...accSteps, 'generating' as PipelineStep];
        patchAssistant({
          // An errored turn already shows `Error: …` when nothing streamed.
          ...(errored ? {} : { content: accContent }),
          sources: accSources.length > 0 ? accSources : undefined,
          streaming: false,
          progressStep: null,
          progressMessage: undefined,
          planningTokens: undefined,
          completedSteps: finalSteps,
          stepDetails: accStepDetails,
          planningEvent: accPlanning,
          routingEvent: accRouting,
          graphTraversalEvent: accGraphEvent,
          profileEvent: accProfile,
          scoreThreshold: accScoreThreshold,
          responseDetectedIocs: accResponseIocs,
          turnId: accTurnId,
        });

        const entry = streamsRef.current.get(sessionId);
        if (entry) {
          const c = ctxRef.current;
          const persisted = !!entry.runId;
          // Graph events and pin edits made while the turn ran.
          if (entry.accGraph !== seededGraph) {
            if (persisted) {
              // The chat row exists server-side from the turn's start (even
              // for a brand-new chat), so write the graph directly.
              updateAccumulatedGraph(sessionId, entry.accGraph as unknown as Record<string, unknown>)
                .then(() => qc.invalidateQueries({ queryKey: chatKey(sessionId) }))
                .catch((err) => console.warn('accumulated graph save failed', err));
            } else {
              c.updateSessionMessagesById(sessionId, messageDataToStored(entry.messages), entry.accGraph);
            }
          }
          if (persisted) c.notifyTurnComplete(sessionId);
          if (!spec.reattached && !spec.startSessionTitle && !errored) {
            const title = deriveTitle(messageDataToStored(entry.messages));
            if (title) c.renameSession(sessionId, title);
          }
        }

        patchEntry((prev) => ({
          ...prev,
          status: errored ? 'error' : cancelled ? 'cancelled' : 'done',
        }));

        // Backgrounded-completion notice: the user is in another chat or
        // on another page. Title resolved now so renames are reflected.
        const viewing =
          activeSessionIdRef.current === sessionId && window.location.pathname === '/chat';
        if (!errored && !cancelled && !viewing) {
          const s = sessionsRef.current.find((x) => x.id === sessionId);
          const title = s?.title || spec.startSessionTitle || userMsg.content.slice(0, 60) || 'Chat';
          toast.success(`Reply ready in “${title}”`, {
            action: {
              label: 'Open',
              onClick: () => {
                ctxRef.current.switchSession(sessionId);
                navigate('/chat');
              },
            },
          });
        }
      };

      void run();
    },
    [notify, qc, navigate, replaceStream, sendCancel],
  );

  const startStream = useCallback(
    (args: StartStreamArgs) => {
      runStream({
        sessionId: args.sessionId,
        open: (signal) => streamChat(args.body, signal),
        initialAccGraph: args.initialAccGraph,
        userMsg: args.userMsg,
        assistantMsg: args.assistantMsg,
        serverChatId: args.serverChatId,
        startSessionTitle: args.startSessionTitle,
        priorUserTurns: args.priorUserTurns,
        reattached: false,
      });
    },
    [runStream],
  );

  const attachRun = useCallback(
    ({ sessionId, runId, query }: AttachRunArgs) => {
      if (seenRunIdsRef.current.has(runId)) return;
      const existing = streamsRef.current.get(sessionId);
      if (existing && isLiveStatus(existing.status)) return;
      // Seed graph + turn index from the chat as the server has it now (the
      // running turn is not persisted yet, so it isn't among the turns).
      const detail = qc.fetchQuery<ChatDetail>({
        queryKey: chatKey(sessionId),
        queryFn: () => getChat(sessionId),
        staleTime: 5_000,
      });
      const graph = detail.then(
        (d) => (d.accumulated_graph as AccumulatedGraph | null) ?? emptyAccumulatedGraph(),
      );
      const prior = detail.then((d) => d.turns.length).catch(() => 0);
      runStream({
        sessionId,
        runId,
        // Ids match what the persisted turn will hydrate to, so the chat
        // can tell when the server copy has caught up.
        userMsg: { id: `${runId}-user`, role: 'user', content: query },
        assistantMsg: {
          id: `${runId}-assistant`,
          role: 'assistant',
          content: '',
          streaming: true,
          completedSteps: [],
          turnId: runId,
        },
        open: (signal) => attachChatRun(runId, 0, signal),
        initialAccGraph: graph,
        serverChatId: sessionId,
        startSessionTitle: undefined,
        priorUserTurns: prior,
        reattached: true,
      });
    },
    [qc, runStream],
  );

  // On mount (page load, tab reopen, login): pick up every turn still
  // running server-side for this client, so its chat shows it live, the
  // sidebar marks it, and its completion toasts.
  useEffect(() => {
    let cancelledEffect = false;
    listChatRuns()
      .then((runs) => {
        if (cancelledEffect) return;
        for (const r of runs) {
          if (r.chat_id) attachRun({ sessionId: r.chat_id, runId: r.run_id, query: r.query });
        }
      })
      .catch((err) => console.warn('listing chat runs failed', err));
    return () => {
      cancelledEffect = true;
    };
  }, [attachRun]);

  // Leaving the app shell (logout) detaches; runs keep going server-side.
  useEffect(() => {
    const controls = controlsRef.current;
    return () => {
      for (const c of controls.values()) c.ctrl.abort();
    };
  }, []);

  const value = useMemo<ActiveStreamsContextValue>(
    () => ({
      subscribe,
      getStream,
      getRunningIds,
      startStream,
      attachRun,
      stopStream,
      dismissStream,
      updateStreamGraph,
      clearStreamUnseenGraph,
    }),
    [subscribe, getStream, getRunningIds, startStream, attachRun, stopStream, dismissStream, updateStreamGraph, clearStreamUnseenGraph],
  );

  return <ActiveStreamsContext.Provider value={value}>{children}</ActiveStreamsContext.Provider>;
}

export function useActiveStreams(): ActiveStreamsContextValue {
  const v = useContext(ActiveStreamsContext);
  if (!v) throw new Error('useActiveStreams must be used within an ActiveStreamsProvider');
  return v;
}

/** Subscribe to a single session's stream. */
export function useActiveStream(sessionId: string | null | undefined): ActiveStream | undefined {
  const { subscribe, getStream } = useActiveStreams();
  const getSnapshot = useCallback(() => getStream(sessionId), [getStream, sessionId]);
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/** Session ids with a live run — for the sidebar's running indicator. */
export function useRunningSessionIds(): ReadonlySet<string> {
  const { subscribe, getRunningIds } = useActiveStreams();
  return useSyncExternalStore(subscribe, getRunningIds, getRunningIds);
}
